import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FetchQuickBooksPeriodReportRequest,
  PeriodReportData,
} from "@/api/fetchQuickBooksPeriodReport/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchQuickBooksPeriodReport } from "@/api/fetchQuickBooksPeriodReport/fetchQuickBooksPeriodReportApi";

interface PeriodReportState {
  report: PeriodReportData | null;
  loading: boolean;
  error: string | null;
}

const initialState: PeriodReportState = {
  report: null,
  loading: false,
  error: null,
};

const periodReportSlice = createSlice({
  name: "periodReport",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setReport(state, action: PayloadAction<PeriodReportData | null>) {
      state.report = action.payload;
    },
    clearReport(state) {
      state.report = null;
      state.error = null;
    },
  },
});

export const { setLoading, setError, setReport, clearReport } =
  periodReportSlice.actions;

export const fetchPeriodReportAction =
  (requestData: FetchQuickBooksPeriodReportRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setReport(null)); // Clear old report before fetching new one
    try {
      const response = await fetchQuickBooksPeriodReport(requestData);
      // fetchQuickBooksPeriodReport returns FetchQuickBooksPeriodReportResponse: { data: PeriodReportData }
      // Access response.data to get the PeriodReportData for the Redux store
      if (response && response.data) {
        dispatch(setReport(response.data));
      } else {
        dispatch(setError("Invalid response format"));
      }
    } catch (error) {
      // Report is already cleared at the start, so error state is clean
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch period report"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default periodReportSlice.reducer;
